import { useRef, useState } from 'react';
import { X, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { cn } from '@/lib/utils';
import { LOSS_REASON_LABELS, type LossReason } from '@/services/opportunities.api';

interface Props {
  onClose: () => void;
  /**
   * `reattackOn` est `null` quand l'utilisateur a choisi « Non » ou laissé le
   * toggle à sa valeur par défaut. Une chaîne ISO date (YYYY-MM-DD) déclenche
   * la création d'une tâche de relance côté backend (refonte 2026-05-21, Q8).
   */
  onSubmit: (
    reason: LossReason,
    comment: string | undefined,
    reattackOn: string | null,
  ) => Promise<void>;
  // ===== State lifé au parent (item #5 dette pipeline V2) =====
  // La saisie (motif, commentaire, toggle relance, date) survit aux
  // fermetures + réouvertures de la modale dans la même session. Le parent
  // détient les 4 useState ; la modale n'a plus que l'état UI éphémère
  // (`submitting`, `error`).
  reason: LossReason;
  setReason: (r: LossReason) => void;
  comment: string;
  setComment: (c: string) => void;
  reattackEnabled: boolean;
  setReattackEnabled: (v: boolean) => void;
  reattackOn: string;
  setReattackOn: (d: string) => void;
}

/**
 * Calcule la date de relance par défaut : `effectiveDate + 10 mois`. Si la
 * date d'effet est dans le passé, on cale sur `today + 10 mois` pour
 * garantir que la tâche ne tombe pas immédiatement en retard.
 *
 * Exporté pour que le parent (`OpportunityDetailView`) puisse initialiser
 * son state `reattackOn` au mount — cohérent avec le lift de state #5.
 */
export function computeDefaultReattackDate(effectiveDate: string): string {
  const eff = new Date(`${effectiveDate}T00:00:00Z`);
  const today = new Date();
  const base = eff.getTime() > today.getTime() ? eff : today;
  const out = new Date(base.getTime());
  out.setUTCMonth(out.getUTCMonth() + 10);
  return out.toISOString().slice(0, 10);
}

export function CloseLostModal({
  onClose,
  onSubmit,
  reason,
  setReason,
  comment,
  setComment,
  reattackEnabled,
  setReattackEnabled,
  reattackOn,
  setReattackOn,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const reattack = reattackEnabled ? reattackOn : null;
      await onSubmit(reason, comment.trim() || undefined, reattack);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="bg-brand/30 fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm animate-fade-in"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Clôturer comme perdue"
        onClick={(e) => e.stopPropagation()}
        className="shadow-modal w-full max-w-md rounded-lg bg-white animate-scale-in"
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="border-border-subtle flex items-center justify-between border-b pb-4">
            <div>
              <h2 className="text-brand text-lg font-semibold">Clôturer comme perdue</h2>
              <p className="text-text-muted mt-0.5 text-xs">
                Sélectionnez le motif. Une opportunité perdue peut être rouverte.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-brand rounded-md p-1 hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <Label htmlFor="reason" required>
              Motif
            </Label>
            <Select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as LossReason)}
            >
              {(Object.keys(LOSS_REASON_LABELS) as LossReason[]).map((k) => (
                <option key={k} value={k}>
                  {LOSS_REASON_LABELS[k]}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <Label htmlFor="comment">Commentaire (optionnel)</Label>
            <Textarea
              id="comment"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Contexte de la perte…"
            />
          </div>

          {/* ===== Section « Réattaquable l'année prochaine ? » =====
              Décision produit Q8 (refonte 2026-05-21) : toggle inline avec
              datepicker conditionnel. Design Stripe-like : 2 cards "Non" /
              "Oui" côte à côte, la sélectionnée gagne un border accent +
              fond accent-50. Hint discret en sous-texte. */}
          <div>
            <Label>Opportunité réattaquable l&apos;année prochaine ?</Label>
            <p className="text-text-muted mb-2 text-xs">
              Si oui, une tâche de relance sera créée et assignée au commercial titulaire de
              l&apos;opportunité.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setReattackEnabled(false)}
                aria-pressed={!reattackEnabled}
                className={cn(
                  'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  !reattackEnabled
                    ? 'border-brand bg-gray-50 text-brand'
                    : 'border-border-subtle text-text-muted hover:border-brand/30 hover:text-brand',
                )}
              >
                Non
              </button>
              <button
                type="button"
                onClick={() => setReattackEnabled(true)}
                aria-pressed={reattackEnabled}
                className={cn(
                  'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                  reattackEnabled
                    ? 'border-accent bg-accent-50 text-accent-600'
                    : 'border-border-subtle text-text-muted hover:border-accent/40 hover:text-accent-600',
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  <RefreshCcw className="h-3.5 w-3.5" />
                  Oui, planifier
                </span>
              </button>
            </div>
            {reattackEnabled && (
              <div className="mt-3 animate-fade-in">
                <Label htmlFor="reattackOn">Date de relance</Label>
                <Input
                  id="reattackOn"
                  type="date"
                  value={reattackOn}
                  onChange={(e) => setReattackOn(e.target.value)}
                />
                <p className="text-text-muted mt-1 text-[11px]">
                  Par défaut, ~10 mois après la date d&apos;effet pour rattraper le prospect avant
                  l&apos;anniversaire de son contrat actuel.
                </p>
              </div>
            )}
          </div>

          {error && <Banner tone="danger">{error}</Banner>}

          <div className="border-border-subtle flex justify-end gap-2 border-t pt-4">
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Confirmer la perte
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
